/**
 * «Рассылки» → «Брони» → «Переезд в Москву»: брони, оформленные до переезда салона, за 3 дня до выдачи
 * получают новый адрес. Число по состоянию — кнопка: таблица ниже показывает ровно эти брони, «Открыть в
 * «Чатах»» — их чаты (`/chats?moved=`). Каналы «пишем» — предпросмотр каскадом отправки, ничего не пишет.
 * Как устроено — `docs/knowledge/rassylka-salon-pereehal.md`, данные — `GET /api/mailing/moved`.
 */
import { useQuery } from "@tanstack/react-query";
import { MessagesSquare } from "lucide-react";
import { Link, useSearchParams } from "react-router";

import { fmtPhone } from "@/lib/callRoute";
import { loadMailingMoved, type MovedKind, type MovedRead, type MovedRow, type MovedState } from "@/lib/mailingMoved";

const ORDER: (MovedState | "all")[] = ["today", "sent", "later", "skip", "cancel", "error", "all"];
const HINT: Record<MovedState | "all", string> = {
  today: "ещё не отправлено — уйдёт сегодня с 10 до 21",
  sent: "сообщение о переезде уже ушло",
  later: "выдача позже — напишем за 3 дня до неё",
  skip: "договор в «Бронь», но сделка не в «Забронировано» (отказ) — не пишем",
  cancel: "задачу сняли: договор ушёл из «Бронь» или выдача прошла",
  error: "площадка не приняла сообщение",
  all: "все брони до переезда и все задачи рассылки",
};
const TONE: Partial<Record<MovedState, string>> = { today: "text-accent-text", sent: "text-ok", error: "text-err" };
const WEEKDAY = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];

function dm(day: string): string {
  return `${day.slice(8, 10)}.${day.slice(5, 7)}`;
}

function weekday(day: string): string {
  return WEEKDAY[new Date(`${day}T12:00:00`).getDay()] ?? "";
}

function daysText(n: number | null): string {
  if (n === null) return "";
  if (n === 0) return "сегодня";
  if (n === 1) return "завтра";
  return `через ${n} дн.`;
}

const READ_HINT =
  "Прочтение видно только у шаблона WhatsApp (статус Wazzup) и у моста (галки). Боты Telegram и MAX о прочтении " +
  "не сообщают — у них видно, ответил ли клиент после сообщения и открыл ли push-уведомление.";
const READ_TONE: Record<MovedRead["state"], string> = {
  read: "text-ok",
  delivered: "text-fg",
  sent: "text-fg-muted",
  failed: "text-err",
  bot: "text-fg-muted",
};

function hm(at: string): string {
  return `${dm(at)} ${at.slice(11, 16)}`;
}

/** Ячейка «Прочитал?»: что сообщила площадка, и косвенно — ответ клиента и открытый push. */
function ReadCell({ v }: { v: MovedRead }) {
  const main = v.state === "read" ? "✓✓ прочитал" : v.state === "delivered" ? "✓ доставлено" : v.state === "bot" ? "бот: не видно" : v.label;
  return (
    <div className="flex flex-col gap-0.5" title={v.label}>
      <span className={READ_TONE[v.state]}>
        {main}
        {v.at && <span className="ml-1.5 text-[12px] text-fg-muted">{hm(v.at)}</span>}
      </span>
      {v.replied && <span className="text-[12px] text-ok">ответил {hm(v.replied)}</span>}
      {v.push_opened && <span className="text-[12px] text-ok">открыл push {hm(v.push_opened)}</span>}
    </div>
  );
}

/** Ссылка строки: чат клиента с той же выборкой слева; чата нет — карточка сделки в «Чатах». */
function rowLink(r: MovedRow, pick: string): string {
  return r.chat_id ? `/chats/${r.chat_id}?moved=${pick}` : `/chats?dial=found-deal-${r.deal}`;
}

const WHO: { id: MovedKind; label: string }[] = [
  { id: "bron", label: "Брони" },
  { id: "zapis", label: "Записанные на примерку" },
];

export function MovedTab() {
  const [params, setParams] = useSearchParams();
  const who: MovedKind = params.get("who") === "zapis" ? "zapis" : "bron";
  const q = useQuery({ queryKey: ["mailing-moved", who], queryFn: () => loadMailingMoved(who), refetchInterval: 60_000 });
  const r = q.data;
  const raw = params.get("state");
  const state = (ORDER as string[]).includes(raw ?? "") ? (raw as MovedState | "all") : "today";
  const pickState = (s: MovedState | "all") => {
    const out = new URLSearchParams(params);
    if (s === "today") out.delete("state");
    else out.set("state", s);
    setParams(out, { replace: true });
  };
  const pickWho = (w: MovedKind) => {
    const out = new URLSearchParams(params);
    if (w === "bron") out.delete("who");
    else out.set("who", w);
    out.delete("state");
    setParams(out, { replace: true });
  };
  // Выборка «Чатов»: у записанных — с приставкой вида (`/chats?moved=zapis:today`).
  const pick = who === "zapis" ? `zapis:${state}` : state;
  const rows = r ? r.rows.filter((x) => state === "all" || x.state === state) : [];
  const showRead = state === "sent" || state === "all";
  const withChat = rows.filter((x) => x.chats.length).length;
  const label = (s: MovedState | "all") => (s === "all" ? "Все" : (r?.states[s] ?? s));
  const th = "whitespace-nowrap px-3 py-2 text-left font-medium";
  const td = "whitespace-nowrap px-3 py-1.5";

  return (
    <section className="flex flex-col gap-3" data-annot="переезд в Москву">
      <div className="flex gap-1" role="group" aria-label="Кого показываем" data-annot="брони или записанные">
        {WHO.map((w) => (
          <button
            key={w.id}
            type="button"
            aria-pressed={who === w.id}
            onClick={() => who !== w.id && pickWho(w.id)}
            className={`h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] ${
              who === w.id ? "border-accent text-fg" : "border-border text-fg-muted hover:text-fg"
            }`}
          >
            {w.label}
          </button>
        ))}
      </div>
      {q.isPending && <div className="text-[12.5px] text-fg-muted">Читаю брони из CRM…</div>}
      {q.isError && <div className="text-[12.5px] text-err">Не удалось получить брони: {String(q.error.message)}</div>}
      {r && (
        <>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-fg-muted" data-annot="правило рассылки переезда">
            <span>
              {who === "zapis"
                ? `${r.moves.map((m) => `${m.city}: записанные в «Ждём клиента» до ${dm(m.date)} ${m.date.slice(11, 16)}`).join("; ")}` +
                  " (до смены адреса в рассылках), примерка сегодня и позже — шаблоном WhatsApp сразу; перед отправкой стадия и время записи — в Bitrix."
                : `${r.moves.map((m) => `${m.city}: брони, оформленные до ${dm(m.date)}`).join("; ")} — новый адрес за ${r.days - 1} дня до выдачи; только сделки в «Забронировано».`}
            </span>
            <span
              className={`rounded-full border px-2 py-0.5 ${r.send_on ? "border-ok text-ok" : "border-warn text-warn"}`}
              title={r.send_on ? "фон шлёт сам раз в 10 минут" : "отправку включают флагом PROKAT_MAILING_MOVED_SEND после «да» владельца"}
            >
              {r.send_on ? "отправка включена" : "отправка выключена — предпросмотр"}
            </span>
            <span className="rounded-full border border-border px-2 py-0.5" title="пишем с 10:00 до 21:00 по времени салона">
              окно 10–21 {r.window ? "открыто" : "закрыто"}
            </span>
          </div>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Состояние броней" data-annot="числа переезда">
            {ORDER.filter((s) => s === "all" || s === "today" || s === "later" || s === "sent" || r.counts[s] > 0).map((s) => (
              <button
                key={s}
                type="button"
                aria-pressed={state === s}
                onClick={() => pickState(s)}
                title={HINT[s]}
                className={`flex items-baseline gap-2 rounded-[var(--radius-card)] border px-3 py-1.5 ${
                  state === s ? "border-accent bg-accent-dim" : "border-border hover:bg-bg-hover"
                }`}
                data-annot={`число «${label(s)}»`}
              >
                <span className="text-[12.5px] text-fg-muted">{label(s)}</span>
                <b className={`text-[16px] tabular-nums ${r.counts[s] ? (s === "all" ? "text-fg" : (TONE[s as MovedState] ?? "text-fg")) : "text-fg-faint"}`}>
                  {r.counts[s]}
                </b>
              </button>
            ))}
          </div>
          {showRead && r.read.sent > 0 && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-[12.5px] text-fg-muted" data-annot="прочтение переезда" title={READ_HINT}>
              <span>
                прочитали <b className={`text-[15px] ${r.read.read ? "text-ok" : "text-fg-faint"}`}>{r.read.read}</b> из{" "}
                {r.read.known}, где прочтение видно (WhatsApp, мост)
              </span>
              <span>
                ответили <b className={`text-[15px] ${r.read.replied ? "text-ok" : "text-fg-faint"}`}>{r.read.replied}</b>
              </span>
              <span>
                открыли push <b className={`text-[15px] ${r.read.push_opened ? "text-ok" : "text-fg-faint"}`}>{r.read.push_opened}</b>
              </span>
              {r.read.sent > r.read.known && <span>в ботах прочтение не видно: {r.read.sent - r.read.known}</span>}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-fg-muted">
            <span>{HINT[state]}</span>
            {rows.length > 0 && (
              <Link
                to={`/chats?moved=${pick}`}
                className="inline-flex items-center gap-1.5 rounded-[var(--radius-ui)] border border-border px-2.5 py-1 text-fg hover:bg-bg-hover"
                title="Список чатов этих клиентов — слева в «Чатах», карточка сделки справа"
                data-annot="открыть в чатах"
              >
                <MessagesSquare size={14} /> Открыть в «Чатах»
                {withChat < rows.length && <span className="text-fg-muted">(с чатом {withChat} из {rows.length})</span>}
              </Link>
            )}
          </div>
          {rows.length === 0 ? (
            <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle px-4 py-3 text-[12.5px] text-fg-muted">
              Броней в этом состоянии нет.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border" data-annot="брони переезда">
              <table className="w-full text-[13px]">
                <thead className="bg-bg-subtle text-[12px] text-fg-muted">
                  <tr>
                    <th className={th}>Сделка</th>
                    <th className={th}>Телефон</th>
                    <th className={th}>{who === "zapis" ? "Примерка" : "Выдача"}</th>
                    <th className={th}>{state === "sent" ? "Отправлено" : "Напишем"}</th>
                    <th className={th}>Канал</th>
                    {showRead && (
                      <th className={th} title={READ_HINT}>
                        Прочитал?
                      </th>
                    )}
                    <th className={`${th} w-full`}>Почему</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((x) => (
                    <tr key={`${x.phone}-${x.deal}`} className="border-t border-border hover:bg-bg-hover">
                      <td className={td}>
                        <Link
                          to={rowLink(x, pick)}
                          className="font-medium text-accent-text hover:underline"
                          title={x.chat_id ? "Открыть чат клиента" : "Чата нет — карточка сделки в «Чатах»"}
                        >
                          № {x.deal}
                        </Link>
                        {state === "all" && <span className="ml-2 text-[12px] text-fg-muted">{label(x.state)}</span>}
                      </td>
                      <td className={`${td} tabular-nums`}>{fmtPhone(x.phone)}</td>
                      <td className={`${td} tabular-nums`}>
                        {x.start ? (
                          <>
                            {dm(x.start)} <span className="text-fg-muted">{weekday(x.start.slice(0, 10))}</span>{" "}
                            {x.start.length > 10 && x.start.slice(11, 16) !== "00:00" && <span>{x.start.slice(11, 16)} </span>}
                            <span className="text-[12px] text-fg-muted">{daysText(x.days)}</span>
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className={`${td} tabular-nums text-fg-muted`}>
                        {x.when ? (x.when.length > 10 ? `${dm(x.when)} ${x.when.slice(11, 16)}` : x.when === r.today ? "сегодня" : dm(x.when)) : "—"}
                      </td>
                      <td className={td}>{x.channel || "—"}</td>
                      {showRead && <td className={`${td} text-[12.5px]`}>{x.read ? <ReadCell v={x.read} /> : "—"}</td>}
                      <td className={`${td} whitespace-normal text-[12px] text-fg-muted`}>{x.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
